import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, useToast } from "barte-design-system";
import { useQueryClient } from "@tanstack/react-query";
import { UploadIcon } from "@phosphor-icons/react";

import {
    useGetCompanyPlans,
    useCreateCompanyPlan,
    useUpdateCompanyPlan,
    useAssociateCompanyPlan,
} from "@shared/services";
import { Skeleton, Modalize } from "@shared/components";
import { ModalCompanyPlanUpload } from "../modal-company-plan-upload";

import { BACKOFFICE_QUERIES } from "@backoffice/services";

import {
    ICompanyPlan,
    ICompanyPlanTaxByMcc,
    ICompanyPlanTaxMethodDetail,
    ICompanyPlanTaxCreditInstallment,
} from "@shared/types";
import { ModalCompanyPlanProps } from "./modal-company-plan.type";

import {
    MccTabs,
    CardPix,
    CardBoleto,
    CardDebitCard,
    CardCreditCard,
    ModalConfirmSave,
} from "./components";

import styles from "./modal-company-plan.module.css";

export const ModalCompanyPlan = ({
    isOpen,
    onClose,
    companyPlanId,
    companyId,
    initialData,
}: ModalCompanyPlanProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const toast = useToast()

    const [dataForm, setDataForm] = useState({} as Partial<ICompanyPlan>);
    const [initEdit, setInitEdit] = useState(false);

    const [activeTab, setActiveTab] = useState<string>("");
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

    const { data, isLoading: isLoadingQuery, refetch } = useGetCompanyPlans({
        planId: companyPlanId,
        companyId,
    });
    const isLoading = !!companyPlanId && isLoadingQuery;


    const { mutateAsync: createCompanyPlan, isPending: isPendingCreate } =
        useCreateCompanyPlan({ companyId });
    const { mutateAsync: updateCompanyPlan, isPending: isPendingUpdate } =
        useUpdateCompanyPlan({ companyId });
    const { mutateAsync: associateCompanyPlan } = useAssociateCompanyPlan({
        companyId,
    });


    const onHandleChange = (key: keyof ICompanyPlan, newValue: string) => {
        setDataForm({ ...dataForm, [key]: newValue });
    };

    const handleTaxChange = (
        taxKey: keyof ICompanyPlanTaxByMcc,
        key: string,
        value: string | number,
    ) => {
        if (!activeTab) return;

        const currentMccs = { ...(dataForm.tax?.taxConfigsByMcc || {}) };
        const activeMccData = currentMccs[activeTab] || {};
        const currentDelay = getDelayForRedeem(taxKey) || 0;

        const taxData =
            (activeMccData[taxKey] as Record<string, ICompanyPlanTaxMethodDetail>) ||
            {};
        const delayData = taxData[currentDelay] || {};

        currentMccs[activeTab] = {
            ...activeMccData,
            [taxKey]: {
                ...taxData,
                [currentDelay]: {
                    ...delayData,
                    [key]: value,
                },
            },
        };

        setDataForm({
            ...dataForm,
            tax: {
                ...dataForm.tax,
                taxConfigsByMcc: currentMccs,
            },
        });
    };

    const getDelayForRedeem = (taxKey: keyof ICompanyPlanTaxByMcc) => {
        if (!activeTab) return undefined;

        const currentMccs = { ...(dataForm.tax?.taxConfigsByMcc || {}) };
        const activeMccData = currentMccs[activeTab] || {};

        const taxObject = activeMccData?.[taxKey];
        const taxObjectKeys = Object.keys(taxObject || {});

        return taxObjectKeys?.[0] ? +taxObjectKeys?.[0] : undefined;
    };

    const delayForRedeemPix = getDelayForRedeem("pix");
    const delayForRedeemBankSlip = getDelayForRedeem("bankSlip");
    const delayForRedeemDebit = getDelayForRedeem("debit");

    const onChangeDelayForRedeem = (
        taxKey: keyof ICompanyPlanTaxByMcc,
        value: number,
    ) => {
        if (!activeTab) return;

        const currentDelay = getDelayForRedeem(taxKey);

        const currentMccs = { ...(dataForm.tax?.taxConfigsByMcc || {}) };
        const activeMccData = currentMccs[activeTab] || {};
        const currentValue = activeMccData[taxKey]?.[currentDelay];

        currentMccs[activeTab] = {
            ...activeMccData,
            [taxKey]: {
                [value]: currentValue,
            },
        };

        setDataForm({
            ...dataForm,
            tax: {
                ...dataForm.tax,
                taxConfigsByMcc: currentMccs,
            },
        });
    };

    const handleCreditCardChange = (
        newData: Record<string, ICompanyPlanTaxCreditInstallment>,
    ) => {
        if (!activeTab) return;

        const currentMccs = { ...(dataForm.tax?.taxConfigsByMcc || {}) };
        currentMccs[activeTab] = {
            ...currentMccs[activeTab],
            creditCard: newData,
        };

        setDataForm({
            ...dataForm,
            tax: {
                ...dataForm.tax,
                taxConfigsByMcc: currentMccs,
            },
        });
    };

    const onHandleSubmitCSV = (data: Partial<ICompanyPlan>) => {
        const newMccs = data.tax?.taxConfigsByMcc || {};
        const currentMccs = { ...(dataForm.tax?.taxConfigsByMcc || {}) };

        Object.keys(newMccs).forEach((mcc) => {
            const existingMcc = currentMccs[mcc] || {};
            const newMcc = newMccs[mcc]!;

            currentMccs[mcc] = {
                ...existingMcc,
                pix: { ...existingMcc.pix, ...newMcc.pix },
                bankSlip: { ...existingMcc.bankSlip, ...newMcc.bankSlip },
                debit: { ...existingMcc.debit, ...newMcc.debit },
                creditCard: { ...existingMcc.creditCard, ...newMcc.creditCard },
            };
        });

        setDataForm((prev) => ({
            ...prev,
            tax: {
                ...prev.tax,
                taxConfigsByMcc: currentMccs,
            },
        }));

        setIsUploadModalOpen(false);
    };

    const onHandleSubmit = async () => {
        if (companyPlanId) {
            setIsConfirmModalOpen(true);
            return;
        }

        await handleConfirmSave();
    };

    const handleConfirmSave = async (description?: string) => {
        const isCreate = !companyPlanId;

        const mutateAsync = isCreate ? createCompanyPlan : updateCompanyPlan;

        const response = await mutateAsync({
            planId: companyPlanId,
            payload: {
                ...dataForm,
                ...(description && { description }),
            },
        });

        if (response.success) {
            if (isCreate) {
                const responseAssociate = await associateCompanyPlan({
                    plans: ["id" in response.data ? +response.data.id : 0],
                    isDefault: false,
                });

                if (!responseAssociate.success) {
                    toast.error({
                        title: responseAssociate?.data && 'errors' in responseAssociate.data ? responseAssociate.data.errors[0]?.title : undefined,
                        description: responseAssociate?.message,
                    });

                    return
                }
            }

            toast.success({
                title: t(isCreate ? 'backoffice.pageModalCompanyPlanFormCreateSuccess' : 'backoffice.pageModalCompanyPlanFormUpdateSuccess'),
            });

            queryClient.refetchQueries({ queryKey: [BACKOFFICE_QUERIES.COMPANY_PLANS_BY_SELLER] });
            refetch();

            if (companyPlanId) {
                setIsConfirmModalOpen(false);
            }

            onClose();
        } else {
            toast.error({
                title: response?.data && 'errors' in response.data ? response.data.errors[0]?.title : undefined,
                description: response?.message,
            });
        }
    };



    useEffect(() => {
        if (!isOpen) {
            setDataForm({});
            setInitEdit(false);
            setActiveTab("");
            return;
        }

        if (!initEdit) {
            const sourceData = companyPlanId ? data?.data : initialData;

            if (sourceData) {
                const deepCopy = JSON.parse(JSON.stringify(sourceData));
                setDataForm(deepCopy as Partial<ICompanyPlan>);

                const mccs = Object.keys(deepCopy.tax?.taxConfigsByMcc || {}).sort(
                    (a, b) => Number(a) - Number(b),
                );
                if (mccs.length > 0) {
                    setActiveTab(mccs[0]);
                }
                setInitEdit(true);
            }
        }
    }, [isOpen, data, initialData, companyPlanId, initEdit]);

    return (
        <Modalize
            isOpen={isOpen}
            onClose={onClose}
            title={dataForm.name || ""}
            footer={{
                primaryButton: {
                    children: t("payfac.pageRatePlanModalSaveButton"),
                    onClick: onHandleSubmit,
                    disabled: isLoading || isPendingCreate || isPendingUpdate,
                    type: "button",
                },
                secondaryButton: {
                    children: t("payfac.pageRatePlanModalCancelButton"),
                    variant: "outline",
                    onClick: onClose,
                    disabled: isLoading || isPendingCreate || isPendingUpdate,
                    type: "button",
                },
                buttonLayout: "hug",
            }}
        >
            {isLoading ? <Skeleton height="2.5rem" width="10rem" /> : (
                <Input
                    label={t("backoffice.pageModalCompanyPlanNameLabel")}
                    value={dataForm.name}
                    onChange={(e) => onHandleChange("name", e.target.value)}
                />
            )}

            <div className={styles["mcc-wrapper"]}>
                <MccTabs
                    data={dataForm}
                    activeTab={activeTab}
                    isLoading={isLoading}
                    onChangeData={setDataForm}
                    onTabChange={setActiveTab}
                />

                <Button
                    children={t("backoffice.pageCompanyPlansButtonImport")}
                    iconLeft={<UploadIcon />}
                    onClick={() => setIsUploadModalOpen(true)}
                    loading={isLoading || isPendingCreate || isPendingUpdate}
                    type="button"
                />
            </div>

            {activeTab && (
                <div className={styles["component-wrapper"]}>
                    <CardPix
                        data={
                            dataForm.tax?.taxConfigsByMcc?.[activeTab]?.pix?.[
                            delayForRedeemPix ?? 0
                            ]
                        }
                        onChange={(key, value) => handleTaxChange("pix", key, value)}
                        isLoading={isLoading}
                        delayForRedeem={delayForRedeemPix}
                        onChangeDelayForRedeem={(value) =>
                            onChangeDelayForRedeem("pix", value)
                        }
                    />

                    <CardBoleto
                        data={
                            dataForm.tax?.taxConfigsByMcc?.[activeTab]?.bankSlip?.[
                            delayForRedeemBankSlip ?? 0
                            ]
                        }
                        onChange={(key, value) => handleTaxChange("bankSlip", key, value)}
                        isLoading={isLoading}
                        delayForRedeem={delayForRedeemBankSlip}
                        onChangeDelayForRedeem={(value) =>
                            onChangeDelayForRedeem("bankSlip", value)
                        }
                    />

                    <CardDebitCard
                        data={
                            dataForm.tax?.taxConfigsByMcc?.[activeTab]?.debit?.[
                            delayForRedeemDebit ?? 0
                            ]
                        }
                        onChange={(key, value) => handleTaxChange("debit", key, value)}
                        isLoading={isLoading}
                        delayForRedeem={delayForRedeemDebit}
                        onChangeDelayForRedeem={(value) =>
                            onChangeDelayForRedeem("debit", value)
                        }
                    />


                    <CardCreditCard
                        data={dataForm.tax?.taxConfigsByMcc?.[activeTab]?.creditCard}
                        isLoading={isLoading}
                        onChangeData={handleCreditCardChange}
                    />
                </div>
            )}

            <ModalConfirmSave
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={handleConfirmSave}
                isLoading={isPendingUpdate}
            />

            <ModalCompanyPlanUpload
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                companyId={companyId}
                onHandleSubmit={onHandleSubmitCSV}
            />
        </Modalize>
    );
};
